import React from "react"
import { ChangeEvent, KeyboardEvent, useRef, useEffect, MouseEvent, ReactNode } from "react"

interface MessageInputProps {
  message: string
  placeholder?: string
  disabled?: boolean
  onMessageChange: (e: ChangeEvent<HTMLTextAreaElement>) => void
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void
  onHeightChange?: (height: number) => void
  leftButtons?: ReactNode
  rightButtons?: ReactNode
  className?: string
}

export default function MessageInput({
  message,
  placeholder = "Send a message.",
  disabled = false,
  onMessageChange,
  onKeyDown,
  onHeightChange,
  leftButtons,
  rightButtons,
  className = ""
}: MessageInputProps): React.JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Adjust textarea height based on content
  const adjustHeight = () => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = "auto"
      const newHeight = Math.min(textarea.scrollHeight, 200) // Max height of 200px
      textarea.style.height = `${newHeight}px`

      // Report the container's new height if callback provided
      if (onHeightChange && containerRef.current) {
        onHeightChange(containerRef.current.offsetHeight)
      }
    }
  }

  // Adjust height when message changes
  useEffect(() => {
    adjustHeight()
  }, [message])

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onMessageChange(e)
  }

  const handleContainerClick = (e: MouseEvent<HTMLDivElement>) => {
    // Check if the click target is a button or is inside a button
    const targetElement = e.target as HTMLElement
    const isButton = targetElement.tagName === "BUTTON" || targetElement.closest("button") !== null

    // Only focus if not clicking on a button
    if (!isButton && textareaRef.current) {
      textareaRef.current.focus()
    }
  }

  return (
    <div
      ref={containerRef}
      className={`flex flex-col bg-white border-t shadow-lg p-4 border border-gray-200 rounded-lg mx-4 mb-4 ${className}`}
      onClick={handleContainerClick}
    >
      <textarea
        id="message-input"
        ref={textareaRef}
        className="text-base text-gray-700 placeholder-gray-400 border-none focus:outline-none focus:ring-0 focus:border-transparent transition-all resize-none min-h-12 max-h-[200px] overflow-y-auto px-0 py-0"
        placeholder={placeholder}
        value={message}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        rows={1}
        disabled={disabled}
      />

      {(leftButtons || rightButtons) && (
        <div className="flex gap-3 mt-2 justify-between">
          <div className="flex items-center gap-3">{leftButtons}</div>
          <div className="flex gap-3">{rightButtons}</div>
        </div>
      )}
    </div>
  )
}
